import { motion } from "framer-motion";
import { Check, CircleAlert, HelpCircle, Ban } from "lucide-react";

const STATUS_META = {
  appropriate: {
    label: "Appropriate",
    icon: Check,
    pill: "bg-[#E6EAE3] text-[#284A3B] border-[#C3CBB9]",
    accent: "text-[#284A3B]",
  },
  contemplated: {
    label: "In NY Guidance",
    icon: CircleAlert,
    pill: "bg-[#F3EFE9] text-[#B35A42] border-[#E6D8CF]",
    accent: "text-[#B35A42]",
  },
  confirm: {
    label: "Confirm Scope",
    icon: HelpCircle,
    pill: "bg-[#FDFBF7] text-[#4A5D54] border-[#D5DACF]",
    accent: "text-[#4A5D54]",
  },
  exclude: {
    label: "Out of Scope",
    icon: Ban,
    pill: "bg-[#1A2621] text-[#FDFBF7] border-[#1A2621]",
    accent: "text-[#1A2621]",
  },
};

export default function Scope({ site }) {
  const s = site.scope;
  if (!s) return null;

  return (
    <section
      id="scope"
      data-testid="care-scope"
      className="py-24 md:py-32 bg-[#FDFBF7] border-t border-[#D5DACF]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <span className="eyebrow">Scope of Services</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.03] text-[#1A2621]">
            What Heritage
            <span className="italic text-[#284A3B]"> Companion Care NY</span>{" "}
            focuses on.
          </h2>
          <p className="mt-6 text-lg text-[#4A5D54] leading-relaxed">
            {s.intro}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 rounded-2xl border border-[#D5DACF] bg-white overflow-hidden"
        >
          <div className="hidden md:grid grid-cols-12 px-7 py-4 bg-[#E6EAE3] text-[11px] uppercase tracking-[0.22em] text-[#4A5D54]">
            <div className="col-span-6">Service</div>
            <div className="col-span-4">Initial approach</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          <ul className="divide-y divide-[#D5DACF]">
            {s.items.map((item, i) => {
              const meta = STATUS_META[item.status] || STATUS_META.confirm;
              const Icon = meta.icon;
              return (
                <li
                  key={item.service}
                  data-testid={`scope-row-${i}`}
                  className="px-6 md:px-7 py-6 md:py-7 grid md:grid-cols-12 gap-3 md:gap-6 items-start md:items-center hover:bg-[#F3EFE9]/60 transition-colors"
                >
                  <div className="md:col-span-6 font-serif-display text-xl md:text-2xl text-[#1A2621] leading-snug">
                    {item.service}
                  </div>
                  <div className={`md:col-span-4 text-sm md:text-base ${meta.accent} leading-relaxed`}>
                    {item.approach}
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.14em] ${meta.pill}`}
                    >
                      <Icon size={12} />
                      {meta.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </motion.div>

        <p className="mt-6 text-xs text-[#4A5D54] max-w-3xl">
          Our scope is intentionally limited to companionship and
          lifestyle-support services. For hands-on personal care or any
          medical/nursing task, please contact our New Jersey affiliate
          Heritage Homecare or a licensed home-health agency.
        </p>
      </div>
    </section>
  );
}
